import { Link } from "react-router-dom";

import { useIdentityStatus } from "@/lib/api/hooks";
import { Icon } from "@/lib/Icon";
import type { PrescriptionPurchase } from "@/types/api";

// Same destination as the Home "Upload Government ID" task.
const ID_UPLOAD_HREF = "/app/clinic?tab=documents&upload=id";

// Orders the clinician worklist holds as "Missing ID" while the identity gate
// applies and no ID is on file (backend `prescription_stage`).
const ID_GATED_STATUSES = new Set(["awaiting_prescription", "pending_review"]);

/**
 * TreatmentImage — thumbnail for the Treatment Updates list. A real product
 * photo, cover-filled on a light square (deliberately NOT the task-card's
 * dark contained illustration). Falls back to a mark when no image resolves.
 */
function TreatmentImage({ src, alt }: { src: string | null; alt: string }) {
  return (
    <div className="shrink-0 self-stretch w-[10rem] bg-sunken flex items-center justify-center overflow-hidden">
      {src ? (
        <img src={src} alt={alt} className="h-full w-full object-cover" />
      ) : (
        <div aria-hidden className="w-30 text-ink-muted">
          <Icon name="Mark Line" viewBox="0 0 32 30" />
        </div>
      )}
    </div>
  );
}

export function InFlightPurchasesCard({
  prescriptions,
  needsSyncConsult,
  awaitingConsult,
}: {
  prescriptions: PrescriptionPurchase[];
  needsSyncConsult: boolean;
  awaitingConsult: boolean;
}) {
  const identity = useIdentityStatus();
  const idMissing =
    identity.data?.enabled === true && identity.data.status !== "on_file";
  return (
    <section className="flex flex-col">
      {/* <div className="flex items-center justify-between gap-10 px-5">
        <h2 className="type-label-md text-ink-secondary">YOUR PURCHASES</h2>
        <Link
          to="/app/orders"
          className="type-label-sm text-ink-muted hover:text-ink"
        >
          ORDERS <ArrowInline />
        </Link>
      </div> */}
      <div className="flex items-center justify-between gap-10 bg-[#ECECEC] p-10 font-mono uppercase text-12 text-[#7C7C7C]">
        <div className="">Treatment Updates</div>
        <div>[{String(prescriptions.length).padStart(2, "0")}]</div>
      </div>
      <ul className="flex flex-col">
        {prescriptions.map((p, i) => {
          const awaitingId =
            idMissing && ID_GATED_STATUSES.has(p.pharmacy_order_status ?? "");
          return (
            <li key={p.id}>
              {/* Gapless stack: drop the top border on every card after the
                  first so adjacent cards share one hairline (no doubling). */}
              <article
                className={`flex items-stretch border border-line-subtle bg-raised${
                  i > 0 ? " border-t-0" : ""
                }`}
              >
                {/* Product thumbnail — resolved server-side by product_id.
                    Distinct from the task-card thumbnail: a product photo,
                    cover-filled on a light square. */}
                <TreatmentImage
                  src={p.hero_image_url ?? null}
                  alt={p.healthie_medication_name ?? ""}
                />
                <div className="flex-1 min-w-0 p-15">
                  <h3 className="font-mono text-15 uppercase leading-110">
                    {p.healthie_medication_name ?? "Prescription"}
                  </h3>
                  <p className="text-14 text-ink-secondary mt-8 leading-110">
                    {awaitingId
                      ? "Upload your photo ID so your clinician can review this order."
                      : pharmacyStatusBlurb(
                          p.pharmacy_order_status,
                          needsSyncConsult,
                          awaitingConsult,
                        )}
                  </p>
                  {awaitingId && (
                    <Link
                      to={ID_UPLOAD_HREF}
                      className="flex w-fit items-center min-h-44 type-label-sm underline underline-offset-2"
                    >
                      Upload ID
                    </Link>
                  )}
                  <span className="inline-block mt-12 type-label-sm text-ink bg-sunken border border-line px-12 py-4">
                    {awaitingId
                      ? "AWAITING ID"
                      : pharmacyStatusLabel(
                          p.pharmacy_order_status,
                          awaitingConsult,
                        )}
                  </span>
                </div>
              </article>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function pharmacyStatusLabel(
  status: string | null | undefined,
  awaitingConsult: boolean,
): string {
  // While the order is held by the consult gate — booked OR not — show
  // AWAITING CONSULT. Only flips to AWAITING RX after the visit
  // actually occurs (backend sets awaiting_consult=false).
  if (status === "awaiting_prescription" && awaitingConsult) {
    return "AWAITING CONSULT";
  }
  switch (status) {
    case "awaiting_prescription":
      return "AWAITING RX";
    case "awaiting_pharmacy_routing":
      return "AWAITING PHARMACY";
    case "pending_review":
    case "pending_approval":
      return "IN REVIEW";
    case "processing":
      return "PROCESSING";
    case "shipped":
      return "SHIPPED";
    case "delivered":
      return "DELIVERED";
    default:
      return (status ?? "—").replace(/_/g, " ").toUpperCase();
  }
}

function pharmacyStatusBlurb(
  status: string | null | undefined,
  needsSyncConsult: boolean,
  awaitingConsult: boolean,
): string {
  // Three sub-states for sync-consult-gated orders, in priority order:
  //   1) Patient still needs to book — point them at the CTA above.
  //   2) Booked but visit hasn't happened — confirm + manage expectation.
  //   3) Visit completed (awaitingConsult=false) — falls through to the
  //      generic AWAITING RX blurb below.
  if (status === "awaiting_prescription" && needsSyncConsult) {
    return "Your medication requires a live visit with a clinician in your state. Book above to unblock.";
  }
  if (status === "awaiting_prescription" && awaitingConsult) {
    return "Your visit is on the calendar. We'll write your script after you meet with your clinician.";
  }
  switch (status) {
    case "awaiting_prescription":
      return "Your clinician is reviewing your information. We'll contact you within 24 hours with updates.";
    case "awaiting_pharmacy_routing":
      return "We're routing your order to a pharmacy that ships to your state.";
    case "pending_review":
    case "pending_approval":
      return "Your clinician is reviewing your intake before finalizing.";
    case "processing":
      return "We're preparing your order for shipment.";
    case "shipped":
      return "On its way.";
    case "delivered":
      return "Delivered.";
    default:
      return "Order in progress.";
  }
}
